import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Check, Loader2, Crown, Rocket, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader, PageLoading } from "@/components/shared";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";

const CYCLES = ["monthly", "yearly", "2year"];
const eur = (n) => "€" + Number(n).toLocaleString("de-DE", { minimumFractionDigits: n % 1 ? 2 : 0, maximumFractionDigits: 2 });

export default function Billing() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [modules, setModules] = useState([]);
  const [cycle, setCycle] = useState("yearly");
  const [busy, setBusy] = useState("");

  const load = () => {
    api.get("/employer/subscription").then((r) => setData(r.data));
    api.get("/employer/invoices").then((r) => setInvoices(r.data.invoices || [])).catch(() => {});
    api.get("/employer/modules").then((r) => setModules(r.data.modules || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  if (!data) return <><PageHeader title={t("bill.title")} /><PageLoading /></>;

  const { plan, plans } = data;
  const isOwner = user?.role === "COMPANY_OWNER";

  const subscribe = async (lookupKey) => {
    setBusy(lookupKey);
    try {
      const r = await api.post("/payments/checkout", { lookup_key: lookupKey, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); setBusy(""); }
  };
  const cancel = async () => {
    try { await api.post("/employer/subscription/cancel"); toast.success(t("bill.cancelOk")); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const managePortal = async () => {
    try { const r = await api.post("/employer/billing-portal", { return_url: window.location.href }); window.location.href = r.data.url; }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const activateModule = async (mk) => {
    setBusy(mk);
    try { const r = await api.post(`/employer/module/${mk}/checkout`, { origin_url: window.location.origin }); window.location.href = r.data.checkout_url; }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); setBusy(""); }
  };
  const sendTest = async () => {
    try { const r = await api.post("/employer/test-reminder"); toast.success(t("x.testSent", { email: r.data.sent_to })); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const previewSample = async () => {
    setBusy("sample");
    try {
      const r = await api.get("/employer/sample-report", { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const statusLabel = plan.status === "active"
    ? (plan.cancel_at_period_end ? t("bill.statusCanceled") : t("bill.statusActive"))
    : plan.active ? t("bill.trialActive") : t("bill.statusExpired");
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : "—";

  const NAME = { starter: t("bill.starterName"), professional: t("bill.proName"), unlimited: t("bill.unlimitedName") };
  const meta = {
    starter: { icon: Rocket, name: NAME.starter,
      feats: [t("bill.upTo", { n: 10 }), t("bill.emailAlerts"), t("bill.workerPortal")] },
    professional: { icon: Crown, name: NAME.professional, highlight: true,
      feats: [t("bill.w30"), t("bill.subs2"), t("bill.buyModules"), t("bill.branded"), t("bill.workerPortal"), t("bill.support")] },
    unlimited: { icon: Building2, name: NAME.unlimited,
      feats: [t("bill.allUnlimited"), t("bill.buyModules"), t("bill.branded"), t("bill.aiYes"), t("bill.support")] },
  };

  return (
    <div data-testid="billing-page">
      <PageHeader title={t("bill.title")} subtitle={t("bill.sub")} />

      <div className="card-soft mb-6 flex flex-wrap items-center justify-between gap-3 p-5" data-testid="current-plan-card">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-400">{t("bill.currentPlan")}</div>
          <div className="mt-1 font-heading text-xl font-bold text-navy">
            {plan.tier ? (NAME[plan.tier] || "—") : "—"}
            <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">{statusLabel}</span>
          </div>
          <div className="mt-1 text-sm text-slate-500">
            {plan.status === "active" && plan.current_period_end && `${t("bill.renewsOn", { date: fmtDate(plan.current_period_end) })}`}
            {plan.status !== "active" && plan.trial_ends_at && t("bill.trialEnds", { date: fmtDate(plan.trial_ends_at) })}
          </div>
        </div>
        {isOwner && data.subscription?.stripe_customer_id && (
          <Button variant="outline" className="rounded-full" onClick={managePortal} data-testid="manage-billing-btn">{t("bill.manageBilling")}</Button>
        )}
        {isOwner && (
          <Button variant="outline" className="rounded-full" onClick={sendTest} data-testid="test-reminder-btn">{t("x.testReminder")}</Button>
        )}
        <Button variant="outline" className="rounded-full" onClick={previewSample} disabled={busy === "sample"} data-testid="preview-sample-btn">
          {busy === "sample" ? <Loader2 className="h-4 w-4 animate-spin" /> : t("bill.previewSample")}
        </Button>
        {isOwner && plan.status === "active" && !plan.cancel_at_period_end && (
          <Button variant="outline" className="rounded-full text-red-600" onClick={cancel} data-testid="cancel-sub-btn">{t("bill.cancel")}</Button>
        )}
      </div>

      {plan.read_only && (
        <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="readonly-banner">{t("bill.readOnly")}</div>
      )}

      {plan.status === "active" && plan.interval === "monthly" && (
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-teal/40 bg-teal/5 px-4 py-3" data-testid="annual-cta">
          <span className="text-sm text-navy">{t("bill.annualCta")}</span>
          <Button size="sm" className="rounded-full bg-teal text-white hover:opacity-90" onClick={() => setCycle("yearly")} data-testid="annual-cta-btn">{t("bill.yearly")}</Button>
        </div>
      )}

      {modules.length > 0 && (
        <div className="mb-8" data-testid="addons-section">
          <h3 className="mb-3 font-heading text-lg font-bold text-navy">{t("addon.title")}</h3>
          <div className="grid gap-4 sm:grid-cols-2">
            {modules.map((mo) => {
              const active = mo.access === "full" || mo.access === "read_only";
              return (
                <div key={mo.module_key} className="card-soft flex items-center justify-between gap-3 p-5" data-testid={`addon-${mo.module_key}`}>
                  <div>
                    <div className="font-heading font-bold text-navy">{mo.name}</div>
                    <div className="mt-0.5 text-sm text-slate-500">{eur(mo.price)} {t("addon.perYear")}</div>
                    <div className="mt-1 text-xs">
                      {mo.access === "full" && <span className="text-emerald-600">{t("addon.active")}{mo.valid_until ? ` · ${t("addon.renews", { date: fmtDate(mo.valid_until) })}` : ""}</span>}
                      {mo.access === "read_only" && <span className="text-amber-600">{t("addon.readOnly")}</span>}
                      {mo.access === "none" && <span className="text-slate-400">{t("addon.none")}</span>}
                    </div>
                  </div>
                  {active ? (
                    isOwner && data.subscription?.stripe_customer_id
                      ? <Button variant="outline" className="rounded-full" onClick={managePortal} data-testid={`addon-manage-${mo.module_key}`}>{t("addon.manage")}</Button>
                      : null
                  ) : (
                    <Button disabled={!isOwner || busy === mo.module_key} onClick={() => activateModule(mo.module_key)} data-testid={`addon-activate-${mo.module_key}`} className="rounded-full bg-cyan text-white hover:bg-cyan-dark">
                      {busy === mo.module_key ? <Loader2 className="h-4 w-4 animate-spin" /> : t("addon.activate")}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="mb-8 inline-flex rounded-full border border-slate-200 bg-white p-1" data-testid="cycle-toggle">
        {CYCLES.map((c) => (
          <button key={c} onClick={() => setCycle(c)} data-testid={`cycle-${c}`}
            className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${cycle === c ? "bg-navy text-white" : "text-slate-500 hover:text-navy"}`}>
            {t(`bill.${c === "2year" ? "twoYear" : c}`)}
            {c !== "monthly" && <span className="ml-1 text-[11px] text-teal">−{c === "yearly" ? 10 : 30}%</span>}
          </button>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {plans.map((p) => {
          const m = meta[p.tier];
          const iv = p.intervals[cycle];
          const Icon = m.icon;
          const isCurrent = plan.tier === p.tier && plan.status === "active" && plan.interval === cycle;
          return (
            <div key={p.tier} className={`card-soft flex flex-col p-7 ${m.highlight ? "ring-2 ring-cyan premium-shadow" : ""}`} data-testid={`plan-card-${p.tier}`}>
              {m.highlight && <div className="mb-3 inline-flex w-fit rounded-full bg-cyan/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-cyan-dark">{t("bill.popular")}</div>}
              <div className="flex items-center gap-2 font-heading text-xl font-bold text-navy"><Icon className="h-5 w-5 text-cyan-dark" /> {m.name}</div>
              <div className="mt-4 font-heading text-4xl font-extrabold text-navy">{eur(iv.per_month)}<span className="text-base font-medium text-slate-400">{t("bill.perMonth")}</span></div>
              <div className="mt-1 text-sm text-slate-500">
                {cycle === "monthly" ? t("bill.cycleMonthNote") : t("bill.billed", { total: eur(iv.total), cycle: cycle === "yearly" ? t("bill.cycleYear") : t("bill.cycleTwoYear") })}
              </div>
              <div className="mt-0.5 text-[11px] text-slate-400" data-testid={`vat-${p.tier}`}>{t("bill.vat")}</div>
              {cycle !== "monthly" && <div className="mt-1 text-xs font-semibold text-teal">{t("bill.saveAmt", { amt: eur(Math.round(p.monthly_base * iv.months - iv.total)) })}</div>}
              <ul className="mt-6 flex-1 space-y-3">
                {m.feats.map((f) => <li key={f} className="flex items-center gap-2 text-sm text-slate-600"><Check className="h-4 w-4 text-teal" /> {f}</li>)}
              </ul>
              <Button disabled={!isOwner || isCurrent || busy === iv.lookup_key} onClick={() => subscribe(iv.lookup_key)} data-testid={`subscribe-${p.tier}`}
                className={`mt-7 w-full rounded-full ${m.highlight ? "bg-cyan text-white hover:bg-cyan-dark" : "bg-navy text-white hover:bg-navy-muted"}`}>
                {busy === iv.lookup_key ? <Loader2 className="h-4 w-4 animate-spin" /> : isCurrent ? t("bill.currentPlan") : t("bill.subscribe")}
              </Button>
            </div>
          );
        })}
        <div className="card-soft flex flex-col p-7" data-testid="plan-card-enterprise">
          <div className="flex items-center gap-2 font-heading text-xl font-bold text-navy"><Building2 className="h-5 w-5 text-cyan-dark" /> {t("bill.entName")}</div>
          <div className="mt-4 font-heading text-4xl font-extrabold text-navy">{t("bill.entPrice")}</div>
          <div className="mt-1 text-sm text-slate-500">{t("bill.entDesc")}</div>
          <div className="flex-1" />
          <a href="mailto:info@employguard.de" className="mt-7"><Button variant="outline" className="w-full rounded-full">{t("bill.contactSales")}</Button></a>
        </div>
      </div>

      {invoices.length > 0 && (
        <div className="mt-8">
          <h3 className="font-heading text-lg font-bold text-navy">{t("bill.invoices")}</h3>
          <div className="mt-3 card-soft divide-y divide-slate-50" data-testid="invoices-list">
            {invoices.map((inv, i) => (
              <div key={i} className="flex items-center justify-between px-4 py-3 text-sm">
                <div><div className="font-semibold text-navy">{inv.number || "—"}</div><div className="text-xs text-slate-400">{inv.created ? new Date(inv.created).toLocaleDateString() : ""} · {inv.status}</div></div>
                <div className="flex items-center gap-3"><span className="font-medium text-navy">{eur(inv.amount)}</span>{inv.pdf && <a href={inv.pdf} target="_blank" rel="noreferrer" className="text-cyan-dark hover:underline" data-testid={`invoice-download-${i}`}>{t("bill.download")}</a>}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
